/**
 * MGT PO Review Modal (iter258/259/260)
 * ─────────────────────────────────────
 * Opens on a submitted PO.  Three-stage view:
 *   1. Read/Edit approved-or-submitted PO (MGT can Edit / Approve / Reject).
 *   2. After APPROVED — [Generate Franchise Invoice] visible to MGT only.
 *   3. Invoice Editor — editable `invoice_rate` distinct from vendor
 *      `purchase_rate`. GST + totals recompute live. Save Draft / Finalize.
 *
 * Backend enforces every permission; the button-hiding here is only for UX.
 * Duplicate final invoices are blocked at the API level.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, X, Check, Ban, FileText, Download, Pencil } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MgtPOReviewModal({ session, poId, isMgt, onClose }) {
  const [po, setPo] = useState(null);
  const [lines, setLines] = useState([]);   // editable copy for MGT
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  // Invoice editor
  const [invPayload, setInvPayload] = useState(null);
  const [invItems, setInvItems] = useState([]);   // editable copy
  const [gstSplit, setGstSplit] = useState("CGST_SGST");
  const [invRemarks, setInvRemarks] = useState("");
  const [invDueDate, setInvDueDate] = useState("");
  const [finalInvoice, setFinalInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/get`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to load PO");
      setPo(d.po);
      setLines((d.po.lines || []).map(l => ({ ...l })));
      setDirty(false);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [session.token, poId]);
  useEffect(() => { load(); }, [load]);

  const totalAmt = useMemo(
    () => lines.reduce((a, l) =>
      a + (Number(l.qty || 0) * Number(l.rate || 0)), 0),
    [lines]
  );

  const updateLine = (idx, field, val) => {
    setLines(rows => {
      const next = rows.slice();
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
    setDirty(true);
  };
  const removeLine = (idx) => {
    setLines(rows => rows.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const addLine = () => {
    setLines(rows => [...rows, {
      item_code: "", item_name: "", category: "", unit: "kg",
      qty: 0, rate: 0, vendor: "", reason: "MGT added",
      purchase_source: "MGT_EDIT",
    }]);
    setDirty(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/mgt-edit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, po_id: poId,
          lines, grouping: po?.grouping || "vendor",
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      toast.success("PO edits saved (MGT_REVIEW).");
      setPo(d.po);
      setLines((d.po.lines || []).map(l => ({ ...l })));
      setDirty(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Approve failed");
      toast.success("PO approved.");
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Reason is required.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/reject`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId, reason: rejectReason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Reject failed");
      toast.success("PO rejected.");
      setShowReject(false);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // iter263 — Return for Correction (MGT-only). Sends the PO back to CM.
  const [showReturn, setShowReturn] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const returnForCorrection = async () => {
    if (!returnReason.trim()) { toast.error("Reason is required."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/return-for-correction`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId,
                                 reason: returnReason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Return failed");
      toast.success("PO returned to Center Manager for correction.");
      setShowReturn(false); setReturnReason("");
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  // Center-Manager resubmit after correction
  const resubmit = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/resubmit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Resubmit failed");
      toast.success("PO resubmitted for MGT review.");
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // ─── Invoice generation ─────────────────────────────────────────
  const openInvoiceEditor = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/franchise-invoice/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Invoice preview failed");
      setInvPayload(d);
      setInvItems((d.items || []).map(x => ({ ...x })));
      setGstSplit(d.gst_split_default || "CGST_SGST");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const invTotals = useMemo(() => {
    let subtotal = 0, disc = 0, taxable = 0, gst = 0, grand = 0;
    invItems.forEach(it => {
      const qty = Number(it.qty || 0);
      const ir = Number(it.invoice_rate || 0);
      const d = Number(it.discount || 0);
      const g = Number(it.gst_percent || 0);
      const base = qty * ir;
      const t = Math.max(base - d, 0);
      const gstAmt = t * g / 100;
      subtotal += base; disc += d; taxable += t;
      gst += gstAmt; grand += t + gstAmt;
    });
    const cgst = gstSplit === "IGST" ? 0 : gst / 2;
    const sgst = gstSplit === "IGST" ? 0 : gst / 2;
    const igst = gstSplit === "IGST" ? gst : 0;
    return {
      subtotal: round2(subtotal), discount: round2(disc),
      taxable: round2(taxable), gst_amount: round2(gst),
      cgst: round2(cgst), sgst: round2(sgst), igst: round2(igst),
      grand_total: round2(grand),
    };
  }, [invItems, gstSplit]);

  const updateInvItem = (idx, field, val) => {
    setInvItems(rows => {
      const next = rows.slice();
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const persistInvoice = async (finalize) => {
    setBusy(true);
    try {
      const url = finalize
        ? `${API}/api/consumption/purchase-planner/franchise-invoice/finalize`
        : `${API}/api/consumption/purchase-planner/franchise-invoice/save-draft`;
      const r = await fetch(url, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, po_id: poId,
          items: invItems, gst_split: gstSplit,
          remarks: invRemarks || undefined,
          due_date: invDueDate || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      if (finalize) {
        toast.success(`Final invoice ${d.invoice.invoice_number} generated.`);
        setFinalInvoice(d.invoice);
        await load();
      } else {
        toast.success("Draft saved.");
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/franchise-invoice/pdf`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, invoice_id: invoiceId }),
      });
      if (!r.ok) throw new Error("PDF download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `FI_${invoiceId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  // Modal
  const isApproved = po?.status === "approved";
  const isRejected = po?.status === "rejected";
  const isInvoiced = !!po?.invoiced;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2"
         data-testid="mgt-po-review-modal">
      <Card className="w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <CardContent className="p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between border-b pb-2">
            <div>
              <h3 className="text-lg font-bold text-slate-800" data-testid="mgt-po-title">
                {po?.po_number || "Loading…"}
              </h3>
              <div className="text-xs text-slate-500 flex gap-2 mt-1">
                <span>Centre: <b>{po?.center || "—"}</b></span>
                <span>· Lines: <b>{po?.line_count ?? "—"}</b></span>
                {isMgt && <span>· Amount: <b>{(po?.total_amount ?? 0).toLocaleString()}</b></span>}
                <Badge className={
                  isApproved ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                  isRejected ? "bg-rose-100 text-rose-800 border-rose-300" :
                  po?.status === "mgt_review" ? "bg-amber-100 text-amber-800 border-amber-300" :
                  "bg-slate-100 text-slate-700 border-slate-300"
                } data-testid="mgt-po-status">{(po?.status || "…").toUpperCase().replace(/_/g," ")}</Badge>
                {isInvoiced && (
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300"
                           data-testid="mgt-po-invoiced">
                    INVOICED · {po.invoice_number}
                  </Badge>
                )}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="mgt-po-close">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* PO Lines editable */}
              <div className="overflow-x-auto">
                <Table className="text-xs" data-testid="mgt-po-lines-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      {isMgt && <TableHead className="text-right">Rate</TableHead>}
                      {isMgt && <TableHead className="text-right">Amount</TableHead>}
                      {isMgt && !isInvoiced && !isRejected && <TableHead></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((l, i) => (
                      <TableRow key={i} data-testid={`mgt-po-line-${i}`}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          {isMgt && !isInvoiced && !isRejected ? (
                            <Input value={l.item_name || ""}
                                     onChange={e => updateLine(i, "item_name", e.target.value)}
                                     className="h-7 text-xs w-40"
                                     data-testid={`mgt-po-line-item-${i}`} />
                          ) : (
                            <div>
                              <div className="font-semibold">{l.item_name}</div>
                              <div className="text-slate-400 text-[10px]">{l.item_code}</div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{l.category || "—"}</TableCell>
                        <TableCell>
                          {isMgt && !isInvoiced && !isRejected ? (
                            <Input value={l.vendor || ""}
                                     onChange={e => updateLine(i, "vendor", e.target.value)}
                                     className="h-7 text-xs w-28"
                                     data-testid={`mgt-po-line-vendor-${i}`} />
                          ) : (l.vendor || "—")}
                        </TableCell>
                        <TableCell>{l.unit || "—"}</TableCell>
                        <TableCell className="text-right">
                          {isMgt && !isInvoiced && !isRejected ? (
                            <Input type="number" step="0.01"
                                     value={l.qty ?? ""}
                                     onChange={e => updateLine(i, "qty", e.target.value)}
                                     className="h-7 text-xs w-20 text-right font-mono"
                                     data-testid={`mgt-po-line-qty-${i}`} />
                          ) : Number(l.qty || 0)}
                        </TableCell>
                        {isMgt && (
                        <TableCell className="text-right">
                          {!isInvoiced && !isRejected ? (
                            <Input type="number" step="0.01"
                                     value={l.rate ?? ""}
                                     onChange={e => updateLine(i, "rate", e.target.value)}
                                     className="h-7 text-xs w-24 text-right font-mono"
                                     data-testid={`mgt-po-line-rate-${i}`} />
                          ) : Number(l.rate || 0).toFixed(2)}
                        </TableCell>
                        )}
                        {isMgt && (
                        <TableCell className="text-right font-mono">
                          {(Number(l.qty || 0) * Number(l.rate || 0)).toFixed(2)}
                        </TableCell>
                        )}
                        {isMgt && !isInvoiced && !isRejected && (
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => removeLine(i)}
                                      data-testid={`mgt-po-line-remove-${i}`}>
                              <X className="w-3 h-3 text-rose-600" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={isMgt ? 7 : 5} className="text-right font-bold">Total</TableCell>
                      {isMgt && (
                        <TableCell className="text-right font-mono font-bold"
                                     data-testid="mgt-po-total">
                          {totalAmt.toFixed(2)}
                        </TableCell>
                      )}
                      {isMgt && !isInvoiced && !isRejected && <TableCell></TableCell>}
                      {!isMgt && (
                        <TableCell className="text-right font-mono font-bold text-slate-400"
                                     data-testid="mgt-po-total">
                          — hidden —
                        </TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* PO Actions */}
              {isMgt && !isRejected && !isInvoiced && (
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  {!isApproved && (
                    <Button size="sm" variant="outline" onClick={addLine}
                              data-testid="mgt-po-add-line">
                      + Add Line
                    </Button>
                  )}
                  {!isApproved && (
                    <Button size="sm" onClick={saveEdit} disabled={busy || !dirty}
                              data-testid="mgt-po-save-edit">
                      <Pencil className="w-3 h-3 mr-1" /> Save Edits
                    </Button>
                  )}
                  {!isApproved && (
                    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800"
                              onClick={approve} disabled={busy || dirty}
                              data-testid="mgt-po-approve">
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  {/* iter263 — Return for Correction */}
                  {!isApproved && !showReturn && !showReject && (
                    <Button size="sm" variant="outline"
                              onClick={() => setShowReturn(true)}
                              className="text-orange-700 border-orange-300"
                              data-testid="mgt-po-return-open">
                      Return for Correction
                    </Button>
                  )}
                  {showReturn && (
                    <div className="w-full flex gap-2 items-center bg-orange-50 border border-orange-200 rounded p-2">
                      <Input placeholder="What must the manager correct? (required)"
                               value={returnReason}
                               onChange={e => setReturnReason(e.target.value)}
                               className="h-8 flex-1"
                               data-testid="mgt-po-return-reason" />
                      <Button size="sm" className="bg-orange-700 hover:bg-orange-800 text-white"
                                onClick={returnForCorrection} disabled={busy}
                                data-testid="mgt-po-return-confirm">
                        Return to Manager
                      </Button>
                      <Button size="sm" variant="ghost"
                                onClick={() => setShowReturn(false)}>Cancel</Button>
                    </div>
                  )}
                  {!isApproved && !showReject && !showReturn && (
                    <Button size="sm" variant="destructive"
                              onClick={() => setShowReject(true)}
                              data-testid="mgt-po-reject-open">
                      <Ban className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  )}
                  {showReject && (
                    <div className="w-full flex gap-2 items-center bg-rose-50 border border-rose-200 rounded p-2">
                      <Input placeholder="Rejection reason (required)"
                               value={rejectReason}
                               onChange={e => setRejectReason(e.target.value)}
                               className="h-8 flex-1"
                               data-testid="mgt-po-reject-reason" />
                      <Button size="sm" variant="destructive"
                                onClick={reject} disabled={busy}
                                data-testid="mgt-po-reject-confirm">
                        Confirm Reject
                      </Button>
                      <Button size="sm" variant="ghost"
                                onClick={() => setShowReject(false)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                  {isApproved && !invPayload && (
                    <Button size="sm"
                              className="bg-indigo-700 hover:bg-indigo-800"
                              onClick={openInvoiceEditor} disabled={busy}
                              data-testid="mgt-generate-franchise-invoice">
                      <FileText className="w-3 h-3 mr-1" /> Generate Franchise Invoice
                    </Button>
                  )}
                </div>
              )}

              {/* Center-manager view — read-only notice */}
              {!isMgt && (
                <div className="border-t pt-3 space-y-2" data-testid="mgt-po-nonmgt-notice">
                  <p className="text-xs text-slate-500">
                    You may view this PO. Approval, editing, and invoice generation
                    are restricted to Management. Purchase rates and totals are
                    not visible at the Center Manager level.
                  </p>
                  {po?.status === "returned_for_correction" && (
                    <div className="bg-orange-50 border border-orange-300 rounded p-2 text-xs text-orange-900"
                          data-testid="cm-returned-banner">
                      <b>Returned for correction:</b> {po?.return_reason || "See audit trail."}
                      <div className="mt-2">
                        <Button size="sm" onClick={resubmit} disabled={busy}
                                  className="bg-orange-700 hover:bg-orange-800 text-white"
                                  data-testid="cm-resubmit-btn">
                          Resubmit for Review
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Audit log */}
              {(po?.audit_log || []).length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-bold text-slate-600 mb-1">Audit Trail</p>
                  <ul className="text-[11px] text-slate-600 space-y-0.5"
                        data-testid="mgt-po-audit">
                    {(po.audit_log || []).map((a, i) => (
                      <li key={i}>
                        <b>{a.action}</b> · {a.by || "—"} · {(a.at || "").slice(0, 19)}
                        {a.note && <> · <span className="text-slate-500">{a.note}</span></>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ═══ INVOICE EDITOR (MGT only, after approval) ═══ */}
              {invPayload && (
                <div className="border-t pt-3 space-y-2" data-testid="mgt-invoice-editor">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-indigo-800">
                      Franchise Invoice Editor
                      {isInvoiced && <span className="ml-2 text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">LOCKED · FINAL</span>}
                    </p>
                    <div className="flex gap-2 items-center text-xs">
                      <Label className="text-xs">GST Split</Label>
                      <select value={gstSplit}
                                disabled={isInvoiced}
                                onChange={e => setGstSplit(e.target.value)}
                                className="h-7 border rounded px-1 text-xs disabled:bg-slate-100"
                                data-testid="mgt-invoice-gst-split">
                        <option value="CGST_SGST">CGST + SGST</option>
                        <option value="IGST">IGST</option>
                      </select>
                    </div>
                  </div>

                  {/* Bill To / Issuer summary */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="border rounded p-2 bg-amber-50" data-testid="mgt-invoice-bill-to">
                      <p className="text-[10px] font-bold text-amber-800">BILL TO (Franchise Owner)</p>
                      <p className="font-semibold">{invPayload.bill_to.legal_entity_name || invPayload.bill_to.franchise_name || "—"}</p>
                      <p className="text-slate-600">{invPayload.bill_to.billing_address || "—"}</p>
                      <p>GSTIN: {invPayload.bill_to.gstin || "—"} · PAN: {invPayload.bill_to.pan || "—"}</p>
                      <p>Centre: {invPayload.bill_to.centre_code} · {invPayload.bill_to.contact || ""} · {invPayload.bill_to.email || ""}</p>
                    </div>
                    <div className="border rounded p-2 bg-orange-50" data-testid="mgt-invoice-issuer">
                      <p className="text-[10px] font-bold text-orange-800">ISSUER</p>
                      <p className="font-semibold">{invPayload.issuer.legal_name}</p>
                      <p className="text-slate-600">{invPayload.issuer.address || "—"}</p>
                      <p>GSTIN: {invPayload.issuer.gstin || "—"} · PAN: {invPayload.issuer.pan || "—"}</p>
                    </div>
                  </div>

                  {/* Editable line items */}
                  <div className="overflow-x-auto">
                    <Table className="text-xs" data-testid="mgt-invoice-items-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Purchase Rate</TableHead>
                          <TableHead className="text-right">Invoice Rate*</TableHead>
                          <TableHead className="text-right">Discount</TableHead>
                          <TableHead className="text-right">GST %</TableHead>
                          <TableHead className="text-right">Line Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invItems.map((it, i) => {
                          const qty = Number(it.qty || 0);
                          const ir = Number(it.invoice_rate || 0);
                          const d = Number(it.discount || 0);
                          const g = Number(it.gst_percent || 0);
                          const base = qty * ir;
                          const t = Math.max(base - d, 0);
                          const lt = t + t * g / 100;
                          return (
                            <TableRow key={i} data-testid={`mgt-inv-line-${i}`}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>
                                <div className="font-semibold">{it.item_name}</div>
                                <div className="text-slate-400 text-[10px]">{it.item_code}</div>
                              </TableCell>
                              <TableCell>{it.unit}</TableCell>
                              <TableCell className="text-right">
                                <Input type="number" step="0.01"
                                         value={it.qty ?? ""}
                                         disabled={isInvoiced}
                                         onChange={e => updateInvItem(i, "qty", e.target.value)}
                                         className="h-7 text-xs w-20 text-right font-mono disabled:bg-slate-100"
                                         data-testid={`mgt-inv-qty-${i}`} />
                              </TableCell>
                              <TableCell className="text-right font-mono text-slate-500">
                                {Number(it.purchase_rate || 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Input type="number" step="0.01"
                                         value={it.invoice_rate ?? ""}
                                         disabled={isInvoiced}
                                         onChange={e => updateInvItem(i, "invoice_rate", e.target.value)}
                                         className={`h-7 text-xs w-24 text-right font-mono ${isInvoiced ? "bg-slate-100" : "bg-yellow-50"}`}
                                         data-testid={`mgt-inv-rate-${i}`} />
                              </TableCell>
                              <TableCell className="text-right">
                                <Input type="number" step="0.01"
                                         value={it.discount ?? ""}
                                         disabled={isInvoiced}
                                         onChange={e => updateInvItem(i, "discount", e.target.value)}
                                         className="h-7 text-xs w-20 text-right font-mono disabled:bg-slate-100"
                                         data-testid={`mgt-inv-disc-${i}`} />
                              </TableCell>
                              <TableCell className="text-right">
                                <Input type="number" step="0.01"
                                         value={it.gst_percent ?? ""}
                                         disabled={isInvoiced}
                                         onChange={e => updateInvItem(i, "gst_percent", e.target.value)}
                                         className="h-7 text-xs w-16 text-right font-mono disabled:bg-slate-100"
                                         data-testid={`mgt-inv-gst-${i}`} />
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold">
                                {lt.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totals */}
                  <div className="flex justify-end">
                    <div className="text-xs w-72 space-y-1" data-testid="mgt-invoice-totals">
                      <TotalRow k="Subtotal" v={invTotals.subtotal} />
                      <TotalRow k="Less: Discount" v={invTotals.discount} />
                      <TotalRow k="Taxable Value" v={invTotals.taxable} bold />
                      {gstSplit === "IGST"
                        ? <TotalRow k="IGST" v={invTotals.igst} />
                        : <>
                            <TotalRow k="CGST" v={invTotals.cgst} />
                            <TotalRow k="SGST" v={invTotals.sgst} />
                          </>}
                      <TotalRow k="GRAND TOTAL" v={invTotals.grand_total} bold accent />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Due Date</Label>
                      <Input type="date" value={invDueDate}
                               onChange={e => setInvDueDate(e.target.value)}
                               className="h-8" data-testid="mgt-invoice-due-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Remarks</Label>
                      <Input value={invRemarks}
                               onChange={e => setInvRemarks(e.target.value)}
                               className="h-8" data-testid="mgt-invoice-remarks" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 border-t pt-3">
                    <Button size="sm" variant="outline"
                              onClick={() => persistInvoice(false)} disabled={busy || isInvoiced}
                              data-testid="mgt-invoice-save-draft">
                      Save Draft
                    </Button>
                    <Button size="sm"
                              className="bg-indigo-700 hover:bg-indigo-800"
                              onClick={() => persistInvoice(true)} disabled={busy || isInvoiced}
                              data-testid="mgt-invoice-finalize">
                      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                      Generate Final Invoice
                    </Button>
                    {finalInvoice && (
                      <Button size="sm" variant="secondary"
                                onClick={() => downloadPdf(finalInvoice.id)}
                                data-testid="mgt-invoice-download-pdf">
                        <Download className="w-3 h-3 mr-1" /> Download PDF
                      </Button>
                    )}
                    {finalInvoice && (
                      <div className="text-xs text-emerald-800 font-semibold flex items-center"
                            data-testid="mgt-invoice-final-number">
                        ✓ Invoice: {finalInvoice.invoice_number}
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500">
                    * Invoice Rate is the price charged to the Franchise Owner and may differ from the vendor Purchase Rate.
                    Once finalised, this PO cannot generate another final invoice.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TotalRow({ k, v, bold, accent }) {
  return (
    <div className={`flex justify-between px-2 py-0.5 ${bold ? "font-bold" : ""} ${accent ? "border-t border-slate-400 text-indigo-800" : ""}`}>
      <span>{k}</span>
      <span className="font-mono">{Number(v || 0).toFixed(2)}</span>
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
